import { useCallback, useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { customerApi, CustomerPin } from '@/api/customers'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'

// 同一客戶的額外訂單查詢 PIN：每位使用者一組，權限與主 PIN 相同，離職時只停用該組
export default function CustomerPinsEditor({ customerId }: { customerId: string }) {
  const [pins, setPins] = useState<CustomerPin[]>([])
  const [name, setName] = useState('')
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try { setPins(await customerApi.listPins(customerId)) } catch { setPins([]) }
  }, [customerId])
  useEffect(() => { load() }, [load])

  const add = async () => {
    setError('')
    if (!name.trim() || pin.trim().length < 4) { setError('請填名稱，PIN 至少 4 碼'); return }
    setBusy(true)
    try {
      await customerApi.createPin(customerId, { name: name.trim(), pin: pin.trim() })
      setName(''); setPin(''); await load()
    } catch (e: any) {
      setError(e?.response?.data?.detail || '新增失敗')
    } finally { setBusy(false) }
  }

  const toggle = async (p: CustomerPin) => { await customerApi.updatePin(customerId, p.id, { is_active: !p.is_active }); load() }
  const remove = async (p: CustomerPin) => {
    if (!window.confirm(`確定刪除「${p.name}」的 PIN？`)) return
    await customerApi.deletePin(customerId, p.id); load()
  }

  return (
    <div className="bg-blue-50/50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800 space-y-3">
      <div>
        <Label className="mb-1 block text-[11px] font-bold text-blue-600 uppercase tracking-wider">其他使用者 PIN</Label>
        <p className="text-xs text-gray-400">每位使用者一組，權限與上方 PIN 相同；人員離職時停用該組即可。</p>
      </div>
      {pins.map(p => (
        <div key={p.id} className="flex items-center gap-2 text-sm">
          <span className={`flex-1 truncate ${p.is_active ? '' : 'text-gray-400 line-through'}`}>{p.name}</span>
          <span className="font-mono text-gray-600">{p.pin}</span>
          <button type="button" className="text-xs px-2 py-1 rounded border border-gray-300 cursor-pointer hover:bg-white" onClick={() => toggle(p)}>{p.is_active ? '停用' : '啟用'}</button>
          <button type="button" className="p-1 text-red-500 cursor-pointer hover:bg-red-50 rounded" onClick={() => remove(p)}><Trash2 size={14} /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="名稱（例：王經理）" maxLength={100} />
        <Input value={pin} onChange={e => setPin(e.target.value)} placeholder="PIN（4～20 碼）" maxLength={20} className="w-36" />
        <Button type="button" size="sm" disabled={busy} onClick={add}><Plus size={14} />新增</Button>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  )
}
